import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { prelovedApi } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { Recycle, Heart, BadgeCheck, ArrowRight } from 'lucide-react';

const CIRCULAR_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/af49a006-9be0-463b-96a6-a9914972e5ea/images/460b3b61009909a200c86a68eea98cc7c769618fb83339600fba47ca3364c975.jpeg';

export default function PreLovedPage() {
  const { t, tField } = useLang();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPreloved = async () => {
      try {
        const response = await prelovedApi.getAll();
        setProducts(response.data.products || []);
      } catch (error) {
        console.error('Error fetching preloved products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPreloved();
  }, []);

  const steps = [
    {
      icon: Recycle,
      title: { it: 'Capi Rigenerati', en: 'Regenerated Garments' },
      text: {
        it: 'I capi restituiti tramite il programma Biolosophy Value vengono controllati, rigenerati e rimessi in circolo.',
        en: 'Garments returned through the Biolosophy Value program are inspected, regenerated and put back into circulation.',
      },
    },
    {
      icon: BadgeCheck,
      title: { it: 'Qualità Garantita', en: 'Guaranteed Quality' },
      text: {
        it: 'Ogni capo Pre-Loved è verificato dai nostri artigiani italiani e garantito come nuovo.',
        en: 'Every Pre-Loved garment is verified by our Italian artisans and guaranteed as new.',
      },
    },
    {
      icon: Heart,
      title: { it: 'Una Seconda Vita', en: 'A Second Life' },
      text: {
        it: 'Scegliere Pre-Loved significa ridurre gli sprechi e dare valore a capi che hanno ancora tanto da raccontare.',
        en: 'Choosing Pre-Loved means reducing waste and giving value to garments that still have stories to tell.',
      },
    },
  ];

  return (
    <div data-testid="preloved-page" className="min-h-screen bg-[#FAF9F6] pt-24">
      {/* Hero */}
      <section className="relative">
        <div className="grid lg:grid-cols-2">
          <div className="px-6 md:px-12 lg:px-24 py-20 flex flex-col justify-center">
            <p className="overline mb-4">Pre-Loved</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#1C1917] mb-8">
              {t({ it: 'Capi con una storia,', en: 'Garments with a story,' })}<br />
              {t({ it: 'pronti per la tua', en: 'ready for yours' })}
            </h1>
            <p className="text-lg text-[#1C1917]/70 leading-relaxed mb-8">
              {t({
                it: 'La collezione Pre-Loved raccoglie i capi biolosophy restituiti dai nostri clienti attraverso il programma Biolosophy Value: analizzati, riparati, rigenerati, lavati e trattati con ozono per sanificarli, pronti per tornare ad essere indossati e amati nuovamente.',
                en: 'The Pre-Loved collection gathers biolosophy garments returned by our customers through the Biolosophy Value program: analysed, repaired, regenerated, washed and ozone-sanitised, ready to be worn and loved again.',
              })}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/club"
                data-testid="preloved-club-btn"
                className="btn-primary inline-flex items-center gap-2"
              >
                {t({ it: 'Scopri il Club Biolosophy', en: 'Discover Club Biolosophy' })}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
          <div className="h-[400px] lg:h-auto">
            <img
              src={CIRCULAR_IMAGE}
              alt={t({ it: 'Economia circolare', en: 'Circular economy' })}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Catalog */}
      <section className="px-6 md:px-12 lg:px-24 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-base md:text-lg font-medium uppercase tracking-widest text-[#4A5D23] mb-2">
              {t({ it: 'La Collezione Pre-Loved', en: 'The Pre-Loved Collection' })}
            </h2>
            <p className="text-[#57534E]">
              {products.length}{' '}
              {products.length === 1
                ? t({ it: 'capo disponibile', en: 'garment available' })
                : t({ it: 'capi disponibili', en: 'garments available' })}{' '}
              — {t({ it: 'pezzi unici, rigenerati e certificati', en: 'one-of-a-kind pieces, regenerated and certified' })}
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-[#E5E0D8] mb-4" />
                  <div className="h-4 bg-[#E5E0D8] w-3/4 mb-2" />
                  <div className="h-4 bg-[#E5E0D8] w-1/2" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div data-testid="preloved-empty" className="text-center py-24 bg-[#F2EFE9]">
              <Recycle className="w-10 h-10 text-[#4A5D23] mx-auto mb-6" />
              <h3 className="text-base md:text-lg font-medium text-[#1C1917] mb-3 uppercase tracking-widest">
                {t({ it: 'Nessun capo disponibile al momento', en: 'No garments available right now' })}
              </h3>
              <p className="text-[#57534E] max-w-md mx-auto mb-6">
                {t({
                  it: 'I capi Pre-Loved arrivano dal programma Biolosophy Value dei nostri clienti. Ogni pezzo è unico: torna a trovarci presto o inizia tu il ciclo!',
                  en: 'Pre-Loved garments come from our customers\' Biolosophy Value returns. Every piece is unique: check back soon or start the cycle yourself!',
                })}
              </p>
              <Link
                to="/club"
                className="text-[#4A5D23] font-medium hover:underline"
              >
                {t({ it: 'Scopri come funziona il Club Biolosophy →', en: 'Find out how Club Biolosophy works →' })}
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {products.map((product, idx) => (
                <Link
                  key={product.product_id}
                  to={`/product/${product.product_id}`}
                  data-testid={`preloved-card-${idx}`}
                  className="group card-hover"
                >
                  <div className="relative aspect-[3/4] bg-[#F2EFE9] mb-4 overflow-hidden">
                    <img
                      src={product.images?.[0] || 'https://via.placeholder.com/400x500'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 bg-[#4A5D23] text-white text-xs px-3 py-1 uppercase tracking-wider">
                      Pre-Loved
                    </span>
                    <span className="absolute top-3 right-3 bg-white/90 text-[#1C1917] text-xs px-3 py-1 uppercase tracking-wider">
                      {t({ it: 'Pezzo Unico', en: 'One of a Kind' })}
                    </span>
                  </div>
                  <p className="text-xs uppercase tracking-wider text-[#57534E] mb-1">
                    {product.category} · {t({ it: 'Rigenerato', en: 'Regenerated' })}
                  </p>
                  <h3 className="font-medium mb-1 group-hover:text-[#4A5D23] transition-colors">
                    {tField(product, 'name')}
                  </h3>
                  <p className="text-[#4A5D23] font-medium">€{product.price.toFixed(2)}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Steps */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="grid md:grid-cols-3 gap-12">
          {steps.map((step) => (
            <div key={step.title.it}>
              <step.icon className="w-8 h-8 text-[#4A5D23] mb-6" />
              <h2 className="text-lg font-medium text-[#1C1917] mb-4">{t(step.title)}</h2>
              <p className="text-sm text-[#1C1917]/70 leading-relaxed">{t(step.text)}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
